/**
 * Identity 卡片组件
 * 用于身份列表页显示
 * 
 * 设计特点：
 * - 圆角方形大头像
 * - hover 时科技感光晕效果
 * - 状态徽章、语料统计、声音状态
 * - 学习中时显示进度条
 */

import { useCallback } from 'react'
import { cn } from '@/shared/lib/utils'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { FileText, Mic2, MoreHorizontal, Trash2, Archive, ArchiveRestore, Edit } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Button } from '@/shared/components/ui/button'
import type { Identity } from '@/modules/identity/types/identity'
import { IdentityStatusBadge, VoiceStatusBadge, CompactProgress } from './StatusBadge'

interface IdentityCardProps {
  identity: Identity
  onClick?: (identity: Identity) => void
  onEdit?: (identity: Identity) => void
  onArchive?: (identity: Identity) => void
  onUnarchive?: (identity: Identity) => void
  onDelete?: (identity: Identity) => void
  className?: string
}

export function IdentityCard({
  identity,
  onClick,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  className,
}: IdentityCardProps) {
  const isLearning = identity.status === 'learning'
  const isArchived = identity.status === 'archived'
  
  // 获取头像首字母
  const avatarFallback = identity.name.slice(0, 2).toUpperCase()

  const handleClick = useCallback(() => {
    onClick?.(identity)
  }, [onClick, identity])

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(identity)
  }, [onEdit, identity])

  const handleArchive = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (isArchived) {
      onUnarchive?.(identity)
    } else {
      onArchive?.(identity)
    }
  }, [onArchive, onUnarchive, identity, isArchived])

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete?.(identity)
  }, [onDelete, identity])

  return (
    <Card
      className={cn(
        'group relative cursor-pointer transition-all duration-300',
        'hover:shadow-lg hover:shadow-violet-500/10',
        'hover:border-violet-200/50',
        // 科技感光晕效果
        'before:absolute before:inset-0 before:rounded-lg before:opacity-0',
        'before:bg-gradient-to-br before:from-violet-500/5 before:via-transparent before:to-purple-500/5',
        'before:transition-opacity before:duration-300',
        'hover:before:opacity-100',
        isArchived && 'opacity-60',
        className
      )}
      onClick={handleClick}
    >
      <CardContent className="p-5">
        {/* 顶部区域：头像 + 操作按钮 */}
        <div className="flex items-start justify-between mb-4">
          {/* 大头像 - 圆角方形 */}
          <Avatar className="h-16 w-16 rounded-xl ring-2 ring-slate-100 group-hover:ring-violet-200/50 transition-all">
            <AvatarImage src={identity.avatar} alt={identity.name} className="object-cover" />
            <AvatarFallback className="rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 text-violet-700 text-lg font-semibold">
              {avatarFallback}
            </AvatarFallback>
          </Avatar>

          {/* 操作菜单 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleArchive}>
                {isArchived ? (
                  <>
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    恢复
                  </>
                ) : (
                  <>
                    <Archive className="h-4 w-4 mr-2" />
                    归档
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* 身份名称 + 状态 */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-900 truncate group-hover:text-violet-700 transition-colors">
              {identity.name}
            </h3>
            <IdentityStatusBadge status={identity.status} size="sm" />
          </div>
          {identity.description && (
            <p className="text-sm text-slate-500 line-clamp-2 min-h-[40px]">
              {identity.description}
            </p>
          )}
        </div>

        {/* 学习进度（仅学习中显示） */}
        {isLearning && identity.learning_progress !== null && identity.learning_progress !== undefined && (
          <div className="mb-3">
            <CompactProgress progress={identity.learning_progress} />
            {identity.estimated_remaining_time && (
              <p className="text-[10px] text-slate-400 mt-1">
                预计剩余：{identity.estimated_remaining_time}
              </p>
            )}
          </div>
        )}

        {/* 底部统计 */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
          {/* 语料数量 */}
          <div className="flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            <span>{identity.corpus_count ?? 0} 条语料</span>
          </div>
          
          {/* 声音状态 */}
          <div className="flex items-center gap-1">
            <Mic2 className="h-3.5 w-3.5" />
            <VoiceStatusBadge status={identity.voice_status || 'none'} size="sm" showIcon={false} />
          </div>
        </div>

        {/* 版本号（右下角） */}
        <div className="absolute bottom-2 right-3 text-[10px] text-slate-300">
          v{identity.version ?? 1}
        </div>
      </CardContent>
    </Card>
  )
}

// ==================== 骨架屏 ====================

export function IdentityCardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="p-5">
        {/* 头像骨架 */}
        <div className="flex items-start justify-between mb-4">
          <div className="h-16 w-16 rounded-xl bg-slate-200" />
        </div>
        
        {/* 标题骨架 */}
        <div className="mb-3">
          <div className="h-5 w-32 bg-slate-200 rounded mb-2" />
          <div className="h-4 w-full bg-slate-100 rounded mb-1" />
          <div className="h-4 w-2/3 bg-slate-100 rounded" />
        </div>
        
        {/* 底部统计骨架 */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="h-3 w-20 bg-slate-100 rounded" />
          <div className="h-3 w-16 bg-slate-100 rounded" />
        </div>
      </CardContent>
    </Card>
  )
}

// ==================== 创建新身份卡片 ====================

interface CreateIdentityCardProps {
  onClick?: () => void
  className?: string
}

export function CreateIdentityCard({ onClick, className }: CreateIdentityCardProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-300 border-dashed border-2',
        'hover:border-violet-300 hover:bg-violet-50/30',
        'flex items-center justify-center min-h-[220px]',
        className
      )}
      onClick={onClick}
    >
      <div className="text-center">
        <div className="h-12 w-12 mx-auto mb-3 rounded-xl bg-violet-100 flex items-center justify-center">
          <span className="text-2xl text-violet-500">+</span>
        </div>
        <p className="text-sm font-medium text-slate-600">创建新身份</p>
        <p className="text-xs text-slate-400 mt-1">开始构建 AI 数字分身</p>
      </div>
    </Card>
  )
}
